// @flow
import * as React from 'react';
import type { Authentication, UserData } from '../../types';
import BusyIndicator from '../Common/BusyIndicator';
import validateToken from '../../utils/actions/validateToken';
import login from '../../utils/actions/login';
import logout from '../../utils/actions/logout';

const AuthenticationContext = React.createContext<Authentication>();

type State = {
  authenticated: boolean,
  clockNumber: number,
  name: string,
  userName: string,
  costCentre: string,
  department: ?string,
  imageUrl: string,
  roles: Array<string>,
  claims: { [key: string]: string },
  requiresPasswordReset: boolean,
  verificationToken: string,
  verificationType: number,
  verificationDetails: string,
  busy: boolean
};

const initialState: State = {
  authenticated: false,
  clockNumber: 0,
  name: '',
  userName: '',
  costCentre: '',
  department: '',
  imageUrl: '',
  roles: [],
  claims: {},
  requiresPasswordReset: false,
  verificationToken: '',
  verificationType: 0,
  verificationDetails: '',
  busy: false
};

class AuthenticationProvider extends React.Component<
  {
    children: React.Node
  },
  State
> {
  state = {
    ...initialState,
    busy: true
  };

  componentDidMount() {
    validateToken()
      .then(result => {
        const { success, ...rest } = result;

        this.setState({
          ...initialState,
          authenticated: success,
          ...rest
        });
      })
      .catch(() => this.setState({ ...initialState }));
  }

  handleLogin = (userName: string, password: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this.setState({ ...initialState, busy: true }, () =>
        login(userName, password)
          .then(result => {
            let verifyLogin: boolean = false;

            if (result.verificationType) {
              this.setState({
                busy: false,
                verificationToken: result.token,
                verificationType: result.verificationType,
                verificationDetails: result.verificationDetails
              });

              verifyLogin = true;
            } else {
              this.handleSetUserData(result);
            }

            resolve(verifyLogin);
          })
          .catch(error => {
            this.setState({ busy: false }, () => reject(error));
          })
      );
    });
  };

  handleSetUserData = (userData: UserData): void =>
    this.setState({
      authenticated: true,
      busy: false,
      ...userData
    });

  handleLogout = (): void => {
    logout();

    this.setState({ ...initialState });
  };

  hasRole = (role: string): boolean => this.state.roles.indexOf(role) > -1;

  hasClaim = (key: string, value: string): boolean => {
    return this.state.claims[key] === value;
  };

  handlePasswordReset = () => {
    this.setState({ requiresPasswordReset: false });
  };

  handleImageChanged = (imageUrl: string) => this.setState({ imageUrl });

  render() {
    const {
      busy,
      authenticated,
      clockNumber,
      name,
      userName,
      costCentre,
      department,
      requiresPasswordReset,
      imageUrl,
      roles,
      verificationToken,
      verificationType,
      verificationDetails
    } = this.state;

    return (
      <>
        {busy && <BusyIndicator text="Authenticating" size="5x" />}
        <AuthenticationContext.Provider
          value={{
            authenticated,
            name,
            userName,
            costCentre,
            department,
            requiresPasswordReset,
            busy,
            clockNumber,
            imageUrl,
            roles,
            verificationToken,
            verificationType,
            verificationDetails,
            hasRole: this.hasRole,
            hasClaim: this.hasClaim,
            hasClaims: Object.keys(this.state.claims).length > 0,
            login: this.handleLogin,
            logout: this.handleLogout,
            handlePasswordReset: this.handlePasswordReset,
            handleImageChanged: this.handleImageChanged,
            setUserData: this.handleSetUserData
          }}
        >
          {this.props.children}
        </AuthenticationContext.Provider>
      </>
    );
  }
}

const AuthenticationConsumer = AuthenticationContext.Consumer;
export {
  AuthenticationContext,
  AuthenticationProvider,
  AuthenticationConsumer
};
